package link.erpango.quip.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.*
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material.rememberScaffoldState
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import link.erpango.quip.features.settings.PressureUnit
import link.erpango.quip.features.settings.SettingsEvent
import link.erpango.quip.features.settings.SettingsViewModel
import link.erpango.quip.theme.QuipColors
import link.erpango.quip.utils.isDebugBuild
import link.erpango.quip.ui.components.QuipCard
import link.erpango.quip.ui.components.QuipDialogGeneric
import link.erpango.quip.ui.components.QuipIconBadge
import link.erpango.quip.ui.components.QuipSectionHeader
import link.erpango.quip.ui.components.QuipTransparentHeader
import org.jetbrains.compose.resources.stringResource
import quip.composeapp.generated.resources.*

@Composable
fun SettingsScreen(
    innerPadding: PaddingValues,
    viewModel: SettingsViewModel,
    onLogout: () -> Unit,
    onBack: () -> Unit,
    onOpenCatalog: (() -> Unit)? = null,
) {
    val settingsState by viewModel.settingsState.collectAsState()
    val settingsEvent by viewModel.settingsEvent.collectAsState()
    val scaffoldState = rememberScaffoldState()

    LaunchedEffect(settingsEvent) {
        when (val event = settingsEvent) {
            is SettingsEvent.SignOutCompleted -> {
                viewModel.onEventHandled()
                onLogout()
            }
            is SettingsEvent.TokenRefreshed -> {
                val message = if (event.success) "Token refrescado correctamente" else "Error al refrescar el token"
                scaffoldState.snackbarHostState.showSnackbar(message)
                viewModel.onEventHandled()
            }
            else -> {}
        }
    }
    var showSignOutDialog by remember { mutableStateOf(false) }
    var showExportDialog by remember { mutableStateOf(false) }
    var showThemeDialog by remember { mutableStateOf(false) }
    var exportedData by remember { mutableStateOf<String?>(null) }
    val scrollState = rememberScrollState()

    Scaffold(
        scaffoldState = scaffoldState,
        backgroundColor = Color.Transparent,
        snackbarHost = { host ->
            SnackbarHost(host) { data ->
                Snackbar(
                    snackbarData = data,
                    backgroundColor = QuipColors.colorPrimary,
                    contentColor = Color.White
                )
            }
        }
    ) {
    Column(
        modifier = Modifier
            .fillMaxSize()
            .background(color = QuipColors.colorBackground)
            .padding(innerPadding)
    ) {
        QuipTransparentHeader {
            Row(verticalAlignment = Alignment.CenterVertically) {
                androidx.compose.material.IconButton(onClick = onBack) {
                    Icon(
                        imageVector = Icons.Default.ArrowBack,
                        contentDescription = "Back",
                        tint = QuipColors.colorText
                    )
                }
                androidx.compose.material.Text(
                    text = stringResource(Res.string.settings),
                    style = MaterialTheme.typography.h4,
                    fontWeight = FontWeight.Bold,
                    color = QuipColors.colorText
                )
            }
        }

        Column(
            modifier = Modifier
                .fillMaxSize()
                .weight(1f)
                .verticalScroll(scrollState)
                .padding(16.dp)
        ) {
            
            // Tarjeta de perfil de usuario simple
            settingsState.user?.let { user ->
                Card(
                    elevation = 0.dp,
                    backgroundColor = Color.White,
                    shape = RoundedCornerShape(12.dp),
                    border = androidx.compose.foundation.BorderStroke(1.dp, QuipColors.colorBackground),
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Row(
                        modifier = Modifier.padding(16.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Surface(
                            modifier = Modifier.size(56.dp),
                            shape = CircleShape,
                            color = QuipColors.colorPrimary.copy(alpha = 0.1f)
                        ) {
                            Box(contentAlignment = Alignment.Center) {
                                Icon(
                                    imageVector = Icons.Default.Person,
                                    contentDescription = null,
                                    tint = QuipColors.colorPrimary,
                                    modifier = Modifier.size(28.dp)
                                )
                            }
                        }
                        Spacer(modifier = Modifier.width(16.dp))
                        Column {
                            Text(
                                text = user.name ?: stringResource(Res.string.user),
                                style = MaterialTheme.typography.subtitle1,
                                fontWeight = FontWeight.Bold,
                                color = Color.Black
                            )
                            Text(
                                text = user.email ?: "",
                                style = MaterialTheme.typography.body2,
                                color = QuipColors.colorTextSecondary
                            )
                            if (!user.id.isNullOrBlank()) {
                                Text(
                                    text = "Tu ID: ${user.id}",
                                    style = MaterialTheme.typography.caption,
                                    color = QuipColors.colorTextSecondary,
                                    modifier = Modifier.clickable {
                                        link.erpango.quip.utils.PlatformUtils.copyToClipboard(user.id!!)
                                    }
                                )
                            }
                        }
                    }
                }
                Spacer(modifier = Modifier.height(24.dp))
            }

                // Apparencia
                QuipSectionHeader(
                    title = stringResource(Res.string.settings_section_appearance), // TODO Add string
                    icon = Icons.Default.ThumbUp // TODO: Better icon
                )
                Spacer(modifier = Modifier.height(8.dp))

                Card(
                    elevation = 0.dp,
                    backgroundColor = Color.White,
                    shape = RoundedCornerShape(12.dp),
                    border = androidx.compose.foundation.BorderStroke(1.dp, QuipColors.colorBackground),
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Column {
                         ImprovedSettingsItem(
                            icon = Icons.Default.Favorite, // Palette icon ideally
                            title = stringResource(Res.string.settings_appearance),
                            subtitle = settingsState.currentTheme,
                            onClick = { showThemeDialog = true }
                        )
                        Divider(color = QuipColors.colorBackground, thickness = 1.dp)
                        ImprovedSettingsItem(
                            icon = Icons.Default.Settings,
                            title = stringResource(Res.string.settings_pressure_unit),
                            subtitle = settingsState.pressureUnit.name,
                            onClick = {
                                val newUnit = if (settingsState.pressureUnit == PressureUnit.BAR) {
                                    PressureUnit.PSI
                                } else {
                                    PressureUnit.BAR
                                }
                                viewModel.updatePressureUnit(newUnit)
                            }
                        )
                        Divider(color = QuipColors.colorBackground, thickness = 1.dp)
                        SettingsSwitchItem(
                            icon = Icons.Default.Notifications,
                            title = stringResource(Res.string.settings_notifications),
                            subtitle = stringResource(Res.string.settings_notifications_subtitle),
                            checked = settingsState.notificationsEnabled,
                            onCheckedChange = { viewModel.toggleNotifications() }
                        )
                        Divider(color = QuipColors.colorBackground, thickness = 1.dp)
                        SettingsSwitchItem(
                            icon = Icons.Default.Notifications,
                            title = stringResource(Res.string.settings_notifications_itv),
                            subtitle = stringResource(Res.string.settings_notifications_itv_subtitle),
                            checked = settingsState.itvNotificationsEnabled,
                            onCheckedChange = { viewModel.toggleItvNotifications() }
                        )
                        Divider(color = QuipColors.colorBackground, thickness = 1.dp)
                        SettingsSwitchItem(
                            icon = Icons.Default.Notifications,
                            title = stringResource(Res.string.settings_notifications_revision),
                            subtitle = stringResource(Res.string.settings_notifications_revision_subtitle),
                            checked = settingsState.revisionNotificationsEnabled,
                            onCheckedChange = { viewModel.toggleRevisionNotifications() }
                        )
                        Divider(color = QuipColors.colorBackground, thickness = 1.dp)
                        SettingsSwitchItem(
                            icon = Icons.Default.Notifications,
                            title = stringResource(Res.string.settings_notifications_spare_part),
                            subtitle = stringResource(Res.string.settings_notifications_spare_part_subtitle),
                            checked = settingsState.sparePartNotificationsEnabled,
                            onCheckedChange = { viewModel.toggleSparePartNotifications() }
                        )
                    }
                }

            Spacer(modifier = Modifier.height(24.dp))

            // Datos
            QuipSectionHeader(
                title = stringResource(Res.string.settings_section_data),
                icon = Icons.Default.Share
            )
            Spacer(modifier = Modifier.height(8.dp))
            
            Card(
                elevation = 0.dp,
                backgroundColor = Color.White,
                shape = RoundedCornerShape(12.dp),
                border = androidx.compose.foundation.BorderStroke(1.dp, QuipColors.colorBackground),
                modifier = Modifier.fillMaxWidth()
            ) {
                ImprovedSettingsItem(
                    icon = Icons.Default.Share,
                    title = stringResource(Res.string.settings_export_data),
                    subtitle = stringResource(Res.string.settings_export_data_subtitle),
                    onClick = { 
                        viewModel.exportAllData { exportedData = it }
                        showExportDialog = true 
                    }
                )
            }

            Spacer(modifier = Modifier.height(24.dp))


            QuipSectionHeader(
                title = stringResource(Res.string.settings_section_debug),
                icon = Icons.Default.Build
            )
            Spacer(modifier = Modifier.height(8.dp))

            Card(
                elevation = 0.dp,
                backgroundColor = Color.White,
                shape = RoundedCornerShape(12.dp),
                border = androidx.compose.foundation.BorderStroke(1.dp, QuipColors.colorBackground),
                modifier = Modifier.fillMaxWidth()
            ) {
                Column {
                    ImprovedSettingsItem(
                        icon = Icons.Default.Share,
                        title = stringResource(Res.string.settings_share_log),
                        subtitle = stringResource(Res.string.settings_share_log_subtitle),
                        onClick = { viewModel.shareLogFile() }
                    )
                    Divider(color = QuipColors.colorBackground, thickness = 1.dp)
                    // Debug (solo visible en builds de debug)
                    if (isDebugBuild) {
                        ImprovedSettingsItem(
                            icon = Icons.Default.Refresh,
                            title = "Refrescar token",
                            subtitle = "Fuerza un refresco manual del token de sesión",
                            onClick = { viewModel.refreshToken() }
                        )
                        if (onOpenCatalog != null) {
                            Divider(color = QuipColors.colorBackground, thickness = 1.dp)
                            ImprovedSettingsItem(
                                icon = Icons.Default.Build,
                                title = "Design System",
                                subtitle = "Catálogo de componentes y estilos",
                                onClick = onOpenCatalog
                            )
                        }
                    }
                }

                Spacer(modifier = Modifier.height(24.dp))
            }

            // Sesión
            androidx.compose.material.OutlinedButton(
                onClick = { showSignOutDialog = true },
                modifier = Modifier
                    .fillMaxWidth()
                    .height(56.dp),
                colors = androidx.compose.material.ButtonDefaults.outlinedButtonColors(
                    contentColor = QuipColors.colorError,
                    backgroundColor = Color.Transparent
                ),
                shape = androidx.compose.foundation.shape.RoundedCornerShape(12.dp),
                border = androidx.compose.foundation.BorderStroke(1.dp, QuipColors.colorError)
            ) {
                Text(
                    text = stringResource(Res.string.settings_sign_out),
                    style = MaterialTheme.typography.button,
                    fontWeight = FontWeight.Bold
                )
            }

            Spacer(modifier = Modifier.height(16.dp))

            // Versión
            Text(
                text = "${settingsState.appVersion}",
                style = MaterialTheme.typography.caption,
                color = QuipColors.colorTextSecondary,
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(16.dp)
                    .wrapContentWidth(Alignment.CenterHorizontally)
            )
        }
    }
    } // Scaffold

    // Diálogo de confirmación de cierre de sesión
    if (showSignOutDialog) {
        QuipDialogGeneric(
            title = stringResource(Res.string.settings_sign_out_title),
            message = stringResource(Res.string.settings_sign_out_message),
            confirmButtonText = stringResource(Res.string.settings_sign_out_confirm),
            dismissButtonText = stringResource(Res.string.cancel),
            onConfirm = {
                showSignOutDialog = false
                viewModel.signOut()
            },
            onDismiss = { showSignOutDialog = false },
            isConfirmColored = false // Cancel button is colored (primary), as sign-out is a "dangerous" action
        )
    }

    // Diálogo de exportación
    if (showExportDialog) {
        AlertDialog(
            onDismissRequest = { showExportDialog = false },
            title = { Text(stringResource(Res.string.settings_export_title), color = Color.Black, fontWeight = FontWeight.Bold) },
            text = {
                Column {
                    Text(stringResource(Res.string.settings_export_message), color = QuipColors.colorText)
                    Spacer(modifier = Modifier.height(8.dp))
                    val previewText = (exportedData ?: "Loading...").let { 
                        if (it.length > 100) it.substring(0, 100) + "..." else it 
                    }
                    Text(
                        text = previewText,
                        style = MaterialTheme.typography.caption,
                        color = QuipColors.colorTextSecondary
                    )
                }
            },
            confirmButton = {
                TextButton(onClick = { showExportDialog = false }) {
                    Text(stringResource(Res.string.close), color = QuipColors.colorPrimary)
                }
            },
            backgroundColor = Color.White,
            shape = RoundedCornerShape(16.dp)
        )
    }

    // Diálogo de selección de tema
    if (showThemeDialog) {
        val schemes = remember { link.erpango.quip.theme.ColorSchemes.allSchemes.keys.toList() }
        Dialog(
            onDismissRequest = { showThemeDialog = false }
        ) {
            Surface(
                shape = RoundedCornerShape(16.dp),
                color = Color.White
            ) {
                Column(
                    modifier = Modifier.padding(0.dp)
                ) {
                    // Header
                    Text(
                        text = stringResource(Res.string.settings_appearance),
                        color = Color.Black,
                        fontWeight = FontWeight.Bold,
                        style = MaterialTheme.typography.h6,
                        modifier = Modifier.padding(start = 24.dp, top = 24.dp, end = 24.dp, bottom = 12.dp)
                    )

                    // Scrollable content
                    Box(
                        modifier = Modifier
                            .height(400.dp) // Strict fixed height to prevent recalculations
                            .fillMaxWidth()
                    ) {
                        androidx.compose.foundation.lazy.LazyColumn(
                            contentPadding = PaddingValues()
                        ) {
                            items(
                                count = schemes.size,
                                key = { index -> schemes[index] }
                            ) { index ->
                                val themeName = schemes[index]
                                Row(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .clickable {
                                            viewModel.updateTheme(themeName)
                                            showThemeDialog = false
                                        }
                                        .padding(vertical = 12.dp, horizontal = 24.dp),
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    val scheme = link.erpango.quip.theme.ColorSchemes.allSchemes[themeName]
                                    Surface(
                                        shape = CircleShape,
                                        color = scheme?.colorPrimary ?: Color.Gray,
                                        modifier = Modifier.size(24.dp),
                                        border = androidx.compose.foundation.BorderStroke(1.dp, Color.Gray)
                                    ) {}
                                    Spacer(modifier = Modifier.width(16.dp))
                                    Text(
                                        text = themeName,
                                        style = MaterialTheme.typography.body1,
                                        fontWeight = if (settingsState.currentTheme == themeName) FontWeight.Bold else FontWeight.Normal,
                                        color = Color.Black
                                    )
                                    if (settingsState.currentTheme == themeName) {
                                        Spacer(modifier = Modifier.weight(1f))
                                        Icon(
                                            imageVector = Icons.Default.Check,
                                            contentDescription = "Selected",
                                            tint = QuipColors.colorPrimary
                                        )
                                    }
                                }
                                Divider(color = Color.LightGray.copy(alpha = 0.5f))
                            }
                        }
                    }
                    
                    // Footer
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(8.dp),
                        horizontalArrangement = Arrangement.End
                    ) {
                        TextButton(onClick = { showThemeDialog = false }) {
                            Text(stringResource(Res.string.close), color = QuipColors.colorPrimary)
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun ImprovedSettingsItem(
    icon: ImageVector,
    title: String,
    subtitle: String? = null,
    onClick: () -> Unit
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick)
            .padding(16.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Surface(
            shape = CircleShape,
            color = QuipColors.colorPrimary.copy(alpha = 0.1f),
            modifier = Modifier.size(40.dp)
        ) {
            Box(contentAlignment = Alignment.Center) {
                 Icon(
                    imageVector = icon,
                    contentDescription = null,
                    tint = QuipColors.colorPrimary,
                    modifier = Modifier.size(20.dp)
                )
            }
        }
        Spacer(modifier = Modifier.width(16.dp))
        Column(modifier = Modifier.weight(1f)) {
            Text(
                text = title,
                style = MaterialTheme.typography.body1,
                fontWeight = FontWeight.SemiBold,
                color = Color.Black
            )
            subtitle?.let {
                Text(
                    text = it,
                    style = MaterialTheme.typography.caption,
                    color = QuipColors.colorTextSecondary
                )
            }
        }
    }
}

@Composable
fun SettingsSwitchItem(
    icon: ImageVector,
    title: String,
    subtitle: String? = null,
    checked: Boolean,
    onCheckedChange: (Boolean) -> Unit
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(16.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
         Surface(
            shape = CircleShape,
            color = QuipColors.colorPrimary.copy(alpha = 0.1f),
            modifier = Modifier.size(40.dp)
        ) {
             Box(contentAlignment = Alignment.Center) {
                Icon(
                    imageVector = icon,
                    contentDescription = null,
                    tint = QuipColors.colorPrimary,
                    modifier = Modifier.size(20.dp)
                )
            }
        }
        Spacer(modifier = Modifier.width(16.dp))
        Column(modifier = Modifier.weight(1f)) {
            Text(
                text = title,
                style = MaterialTheme.typography.body1,
                fontWeight = FontWeight.SemiBold,
                color = Color.Black
            )
            subtitle?.let {
                Text(
                    text = it,
                    style = MaterialTheme.typography.caption,
                    color = QuipColors.colorTextSecondary
                )
            }
        }
        Switch(
            checked = checked,
            onCheckedChange = onCheckedChange,
            colors = SwitchDefaults.colors(
                checkedThumbColor = QuipColors.colorPrimary,
                checkedTrackColor = QuipColors.colorPrimary.copy(alpha = 0.5f)
            )
        )
    }
}
